import { useEffect, useState, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "../hooks/useTheme";
import { UserProfileContext, type UserProfile } from "./UserProfileContextDef";

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Fetch or create profile when user changes
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      setInitialized(false);
      return;
    }

    async function loadProfile() {
      setLoading(true);
      try {
        // Try to fetch existing profile
        const { data, error } = await supabase
          .from("user_profiles")
          .select("*")
          .eq("user_id", user!.id)
          .single();

        if (error && error.code === "PGRST116") {
          // Profile doesn't exist, create one with defaults
          const newProfile: Omit<UserProfile, "created_at" | "updated_at"> = {
            user_id: user!.id,
            dark_mode: theme === "dark",
            lang: (i18n.language.startsWith("fr") ? "fr" : "en") as "en" | "fr",
          };

          const { data: created, error: createError } = await supabase
            .from("user_profiles")
            .insert(newProfile)
            .select()
            .single();

          if (createError) {
            console.error("Failed to create user profile:", createError);
          } else {
            setProfile(created as UserProfile);
          }
        } else if (error) {
          console.error("Failed to fetch user profile:", error);
        } else {
          setProfile(data as UserProfile);
        }
      } catch (err) {
        console.error("Error loading user profile:", err);
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [user, theme, i18n]);

  // Sync profile settings to theme and i18n when profile loads (only once)
  useEffect(() => {
    if (!profile || initialized) return;

    // Sync dark mode
    const profileDark = profile.dark_mode;
    const currentDark = theme === "dark";
    if (profileDark !== currentDark) {
      toggleTheme();
    }

    // Sync language
    const currentLang = i18n.language.startsWith("fr") ? "fr" : "en";
    if (profile.lang !== currentLang) {
      i18n.changeLanguage(profile.lang);
    }

    setInitialized(true);
  }, [profile, initialized, theme, toggleTheme, i18n]);

  const updateProfile = useCallback(
    async (updates: Partial<Pick<UserProfile, "dark_mode" | "lang">>) => {
      if (!user || !profile) return;

      const { data, error } = await supabase
        .from("user_profiles")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) {
        console.error("Failed to update user profile:", error);
        throw error;
      }

      setProfile(data as UserProfile);

      // Sync to local state
      if (updates.dark_mode !== undefined) {
        const currentDark = theme === "dark";
        if (updates.dark_mode !== currentDark) {
          toggleTheme();
        }
      }

      if (updates.lang !== undefined) {
        i18n.changeLanguage(updates.lang);
      }
    },
    [user, profile, theme, toggleTheme, i18n],
  );

  return (
    <UserProfileContext.Provider value={{ profile, loading, updateProfile }}>
      {children}
    </UserProfileContext.Provider>
  );
}
